/**
 * SPEC.md §8 — the app's own chrome theme, kept deliberately separate from
 * the diagram theme (`mermaidRender.ts`'s `DiagramTheme`): "a user may well
 * want dark chrome and a light diagram for export." Always
 * `interfaceTheme`, never the bare word "theme" — §8's naming ban exists
 * precisely because these two concepts were confused before. Never included
 * in `share.ts`'s payload: a shared link must never override the
 * recipient's own chrome preference, so this module has no dependency on —
 * and no caller from — the share/hash path at all.
 *
 * Controlled by a `data-interface-theme="light" | "dark"` attribute on
 * `<html>` (tokens.css's light palette is keyed off it). Absent means "no
 * manual override" — `prefers-color-scheme` decides, live; present means an
 * explicit, persisted choice that wins regardless of the OS preference.
 */
import { readStorage, writeStorage } from './storage';

export const INTERFACE_THEME_STORAGE_KEY = 'plotter:interface-theme';

export type InterfaceTheme = 'light' | 'dark';

function isInterfaceTheme(value: string): value is InterfaceTheme {
  return value === 'light' || value === 'dark';
}

/** null means "no manual override on record" — mirrors initialSource.ts's DraftBuffer/history.ts's HistoryRow validation shape: corrupt or foreign content degrades to null, not a crash. */
export function readPersistedInterfaceTheme(): InterfaceTheme | null {
  const raw = readStorage(INTERFACE_THEME_STORAGE_KEY);
  return raw !== null && isInterfaceTheme(raw) ? raw : null;
}

export function persistInterfaceTheme(theme: InterfaceTheme): void {
  writeStorage(INTERFACE_THEME_STORAGE_KEY, theme);
}

/** Sets or clears the `data-interface-theme` attribute tokens.css's light palette is keyed off. */
export function applyInterfaceTheme(theme: InterfaceTheme | null): void {
  if (theme === null) {
    document.documentElement.removeAttribute('data-interface-theme');
  } else {
    document.documentElement.setAttribute('data-interface-theme', theme);
  }
}

export function systemPrefersLight(): boolean {
  return window.matchMedia('(prefers-color-scheme: light)').matches;
}

// M6.8 (issue #35): the command palette's "Toggle interface theme" entry
// needs to trigger the exact same action `InterfaceThemeToggle.tsx`'s
// button does — not a parallel implementation — and that button's own
// icon needs to stay correct no matter which of the two triggered the
// change. `currentOverride`/`listeners` is a minimal subscribe/notify
// store for that, mirroring `render.ts`'s own subscribe/emit shape
// (this codebase's established pattern for "external mutable state
// multiple UI pieces must reactively agree on").
let currentOverride: InterfaceTheme | null | undefined;
const listeners = new Set<(override: InterfaceTheme | null) => void>();

function ensureHydrated(): InterfaceTheme | null {
  if (currentOverride === undefined) currentOverride = readPersistedInterfaceTheme();
  return currentOverride;
}

/** The current manual override (or null for "follow the OS") — read this, not `readPersistedInterfaceTheme` directly, from any UI that needs to stay in sync with a change made elsewhere. */
export function getInterfaceThemeOverride(): InterfaceTheme | null {
  return ensureHydrated();
}

export function subscribeInterfaceThemeOverride(listener: (override: InterfaceTheme | null) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Flips the current effective theme (override ?? system) to its opposite,
 * persists it as an explicit override, applies the DOM attribute, and
 * notifies every subscriber. The one function both
 * `InterfaceThemeToggle.tsx`'s button and the command palette's equivalent
 * entry call — this is the single toggle action, never duplicated.
 */
export function toggleInterfaceTheme(): void {
  const current = ensureHydrated() ?? (systemPrefersLight() ? 'light' : 'dark');
  const next: InterfaceTheme = current === 'light' ? 'dark' : 'light';
  currentOverride = next;
  persistInterfaceTheme(next);
  applyInterfaceTheme(next);
  for (const listener of listeners) listener(next);
}
